import {useAuth} from '@apps-common/auth-react';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import LoopIcon from '@mui/icons-material/Loop';
import {TabContext, TabList} from '@mui/lab';
import {Box, Button, Tab} from '@mui/material';
import React, {useState} from 'react';
import {useDispatch} from 'react-redux';

import UploadsList from './UploadsList.jsx';
import {showModal} from '@actions/ui';
import {useListUploads} from '@api/asyncIngest.js';
import {trackEvent} from '@app/analytics.js';
import LoadingMessage from '@app/components/common/LoadingMessage';
import {MODALS} from '@app/constants';
import useOrgParams from '@app/hooks/useOrgParams';
import {isUsersOrg} from '@app/util/user';
import DrawerHeader from '@components/common/DrawerHeader';
import EmptyMessage from '@components/common/EmptyMessage';
import ErrorMessage from '@components/common/ErrorMessage';

const UploadsPanel = () => {
  const dispatch = useDispatch();
  const TABS = {
    TO_REVIEW: 'To review',
    UPLOADED: 'Uploaded',
    ERRORS: 'Errors',
  };
  const mapTabsToState = {
    [TABS.TO_REVIEW]: 'toReview',
    [TABS.UPLOADED]: 'uploaded',
    [TABS.ERRORS]: 'errors',
  };
  const [activeTab, setActiveTab] = useState(TABS.TO_REVIEW);
  const onTabChange = (event, newValue) => {
    setActiveTab(newValue);
    trackEvent('Async Upload', {
      action: 'switched tabs',
      tab: newValue,
    });
  };

  const {user} = useAuth();
  const {orgId} = useOrgParams();
  const isUserOrg = isUsersOrg(user?.claims, orgId);

  const {data: uploads, error, isPending} = useListUploads();

  const uploadsByState = uploads?.byState || {
    toReview: [],
    errors: [],
    uploaded: [],
  };

  const uploadButton = () => {
    return (
      <Button
        key="uploadFileButton"
        variant="contained"
        color="primary"
        size="small"
        disabled={!isUserOrg}
        onClick={() => dispatch(showModal(MODALS.UPLOAD))}
        startIcon={<ArrowCircleUpIcon fontSize="small" />}
        sx={{marginLeft: 1.25}}
        data-testid="file-uploads-upload-button"
      >
        Upload
      </Button>
    );
  };

  const renderContent = () => {
    if (isPending) {
      return <LoadingMessage suffix="uploads" />;
    }

    if (error) {
      return <ErrorMessage message="Error loading uploaded files" />;
    }

    if (uploads.list.length === 0) {
      return (
        <EmptyMessage
          icon={<LoopIcon />}
          title="No uploads found"
          actions={isUserOrg ? [uploadButton()] : []}
        />
      );
    }

    const uploadsToDisplay = uploadsByState[mapTabsToState[activeTab]] || [];

    return <UploadsList uploads={uploadsToDisplay} />;
  };

  return (
    <>
      <DrawerHeader
        title="File Uploads"
        action={isUserOrg ? uploadButton() : null}
      />
      <TabContext value={activeTab}>
        <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
          <TabList onChange={onTabChange}>
            <Tab
              label={`${TABS.TO_REVIEW} (${uploadsByState.toReview?.length})`}
              value={TABS.TO_REVIEW}
            />
            <Tab
              label={`${TABS.UPLOADED} (${uploadsByState.uploaded?.length})`}
              value={TABS.UPLOADED}
            />
            <Tab
              label={`${TABS.ERRORS} (${uploadsByState.errors?.length})`}
              value={TABS.ERRORS}
            />
          </TabList>
        </Box>
      </TabContext>
      {renderContent()}
    </>
  );
};

export default UploadsPanel;
